import { useState } from "react";
import type { ChainMetaOutput, TokenMetaOutput } from "~/api/types";
import { TokenSelector } from "~/components/TokenSelector/TokenSelector";
import { useTokenBalance } from "~/hooks/useTokenBalance";
import { useTokenPrice } from "~/hooks/useTokenPrice";
import { useUnifiedWallet } from "~/hooks/useUnifiedWallet";
import type { ChainType } from "~/api/types";

export interface ChainTokenInputProps {
  label: string;
  chain: ChainMetaOutput | null;
  token: TokenMetaOutput | null;
  amount: string;
  onChainSelect: (chain: ChainMetaOutput) => void;
  onTokenSelect: (token: TokenMetaOutput) => void;
  onChainTokenSelect?: (token: TokenMetaOutput, chain: ChainMetaOutput) => void;
  onAmountChange?: (amount: string) => void;
  editable?: boolean;
}

export function ChainTokenInput({
  label,
  chain,
  token,
  amount,
  onChainSelect,
  onTokenSelect,
  onChainTokenSelect,
  onAmountChange,
  editable = false,
}: ChainTokenInputProps) {
  const { address } = useUnifiedWallet();
  const chainType: ChainType = chain?.chainType ?? "evm";
  const { formatted: balance } = useTokenBalance(
    address ?? undefined,
    chain ? Number(chain.chainId) : undefined,
    token?.address,
    token?.decimals,
    chainType,
  );
  const tokenPrice = useTokenPrice(chain?.chainId, token?.address);
  const [showSelector, setShowSelector] = useState(false);

  const numAmount = amount ? parseFloat(amount) : 0;
  const usdValue =
    tokenPrice != null && numAmount > 0
      ? `~$${(tokenPrice * numAmount).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : null;

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--color-border-card-section)] bg-[var(--color-bg-card-section)] p-3">
      {/* Label row */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
          {label}
        </span>
        {token && balance && (
          <span className="text-xs text-[var(--color-text-tertiary)]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="mr-0.5 inline-block align-[-1px]">
              <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
            </svg>
            {balance}
          </span>
        )}
      </div>

      {/* Amount + token selector row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1">
          <input
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={amount}
            onChange={(e) => {
              if (editable && onAmountChange) {
                const val = e.target.value;
                if (/^[0-9]*[.]?[0-9]*$/.test(val)) {
                  onAmountChange(val);
                }
              }
            }}
            readOnly={!editable}
            className="w-full min-w-0 bg-transparent text-[2rem] font-bold text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none"
          />
        </div>

        {/* Pill-shaped token selector button */}
        <button
          onClick={() => setShowSelector(true)}
          className="flex h-[50px] shrink-0 items-center gap-2 rounded-full bg-[var(--color-bg-token-pill)] px-3 py-2 transition-[filter] duration-200 hover:brightness-[0.93]"
        >
          {token ? (
            <>
              {/* Token icon with chain badge overlay */}
              <div className="relative h-8 w-8 shrink-0">
                {token.logoUri ? (
                  <img
                    src={token.logoUri}
                    alt={token.symbol}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] text-xs font-bold text-[var(--color-text-secondary)]">
                    {token.symbol.slice(0, 2)}
                  </div>
                )}
                {chain?.logoUri && (
                  <div className="absolute right-0 bottom-0 h-4 w-4 overflow-hidden rounded-[4px] border border-[var(--color-bg-card-section)] bg-[var(--color-bg-card-section)]">
                    <img
                      src={chain.logoUri}
                      alt={chain.name}
                      className="h-full w-full rounded-[4px]"
                    />
                  </div>
                )}
              </div>
              {/* Symbol + chain name */}
              <div className="flex flex-col items-start">
                <span className="text-base font-bold text-[var(--color-text-primary)]">
                  {token.symbol}
                </span>
                {chain && (
                  <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                    {chain.name}
                  </span>
                )}
              </div>
            </>
          ) : (
            <span className="text-sm font-medium text-[var(--color-text-secondary)]">
              Select token
            </span>
          )}
          {/* Chevron right */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 320 512"
            fill="currentColor"
            className="shrink-0 text-[var(--color-text-tertiary)]"
          >
            <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
          </svg>
        </button>
      </div>

      {/* Footer row: USD estimate + percent pills */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[var(--color-text-tertiary)]">
          {usdValue ?? (amount && amount !== "0" ? "~$..." : "$0.00")}
        </span>
        {editable && onAmountChange && token && balance && (
          <div className="flex gap-1">
            {[25, 50, 75].map((pct) => (
              <button
                key={pct}
                onClick={() => {
                  const raw = parseFloat(balance);
                  if (!isNaN(raw)) onAmountChange(String(raw * pct / 100));
                }}
                className="rounded-full bg-[var(--color-bg-slippage-inactive)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-accent)] hover:text-white"
              >
                {pct}%
              </button>
            ))}
            <button
              onClick={() => onAmountChange(balance)}
              className="rounded-full bg-[var(--color-bg-slippage-inactive)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-accent)] transition-colors hover:bg-[var(--color-accent)] hover:text-white"
            >
              Max
            </button>
          </div>
        )}
      </div>

      {showSelector && (
        <TokenSelector
          chainId={chain?.chainId ?? null}
          open={showSelector}
          onOpenChange={setShowSelector}
          onChainSelect={onChainSelect}
          onSelect={(t, selectedChain) => {
            if (onChainTokenSelect) {
              onChainTokenSelect(t, selectedChain);
            } else {
              onChainSelect(selectedChain);
              onTokenSelect(t);
            }
            setShowSelector(false);
          }}
        />
      )}
    </div>
  );
}
